import { deleteObject, getDownloadURL, listAll, ref, uploadBytes } from "firebase/storage";
import { firebaseStorage } from "~/src/lib/firebase/config";

/**
 * 分析記事の本文に埋め込み可能な画像のMIMEタイプと拡張子の対応表
 */
export const ALLOWED_IMAGE_TYPES: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
};

/**
 * 分析記事の本文画像 最大ファイルサイズ（5MB）
 */
export const MAX_ANALYSIS_IMAGE_SIZE = 5 * 1024 * 1024;

/**
 * アップロード対象の画像形式かどうかを判定する
 */
export function isAllowedImageType(type: string): boolean {
  return type in ALLOWED_IMAGE_TYPES;
}

/**
 * 分析記事の本文画像を格納するCloud Storageフォルダのパスを返す
 *
 * @note 下書き・投稿済み記事は同一の分析記事IDを使い回すため（公開時に下書きIDがそのまま
 *       投稿記事IDになる）、下書き〜投稿済みのライフサイクルを通じて同じフォルダを参照する。
 */
function getAnalysisImagesFolderPath(userId: string, articleId: string): string {
  return `users/${userId}/analysis-body-images/${articleId}`;
}

/**
 * 分析記事の本文画像をCloud Storageへアップロードする
 */
export async function uploadAnalysisBodyImage(
  userId: string,
  articleId: string,
  file: File
): Promise<{ url: string; path: string }> {
  const ext = ALLOWED_IMAGE_TYPES[file.type];
  const path = `${getAnalysisImagesFolderPath(userId, articleId)}/${crypto.randomUUID()}.${ext}`;

  const storageRef = ref(firebaseStorage, path);
  await uploadBytes(storageRef, file, { contentType: file.type });
  const url = await getDownloadURL(storageRef);

  return { url, path };
}

/**
 * 分析記事の本文画像をCloud Storageから削除する
 *
 * @note 既に削除済み（object-not-found）の場合はエラーを無視する。
 *       本文JSONの差分検出により同一画像の削除処理が複数回走る可能性があるため。
 */
export async function deleteAnalysisBodyImage(path: string): Promise<void> {
  try {
    await deleteObject(ref(firebaseStorage, path));
  } catch (error) {
    const code = (error as { code?: string }).code;
    if (code !== "storage/object-not-found") {
      throw error;
    }
  }
}

/**
 * 分析記事に紐づく本文画像をCloud Storageからすべて削除する
 * 記事（下書き／投稿済み）が完全に削除され、どこからも画像が参照されなくなったタイミングで呼び出す。
 */
export async function deleteAnalysisArticleImages(userId: string, articleId: string): Promise<void> {
  const folderRef = ref(firebaseStorage, getAnalysisImagesFolderPath(userId, articleId));
  const { items } = await listAll(folderRef);
  await Promise.all(items.map((item) => deleteAnalysisBodyImage(item.fullPath)));
}
